<template>
    <div class="p-6">
        <!-- Header Section -->
        <div class="mb-6">
            <div class="text-[#111111] flex items-center justify-between">
                <h1 class="text-[24px] font-bold">Объекты</h1>
                <Country @update:country="updateCountry" />
            </div>
            <p class="text-[14px] text-[#111111] mt-1">Управление объектами</p>
        </div>

        <!-- Main Content Section -->
        <div class="flex flex-row gap-6 mt-6">
            <!-- Categories Tree -->
            <div class="w-1/4 bg-white rounded-lg border p-4">
                <div class="flex items-center justify-between mb-4">
                    <h2 class="text-lg font-medium">Категории</h2>
                    <Button v-if="hasPermission('dictionary:categories:manage')" variant="outline" size="sm" @click="handleAddCategory(null)">
                        <Plus class="h-4 w-4 mr-2" />
                        Добавить
                    </Button>
                </div>
                <Skeleton v-if="isLoadingCategories"/>
                <CategoryTree 
                    v-else
                    v-model="categoriesTree" 
                    :active-node-id="activeCategoryId" 
                    @update:active-node-id="handleCategoryChange" 
                    @refresh="loadCategories()"
                />
            </div>
            <!-- Products Table -->
            <div class="flex-1">
                <div class="bg-white rounded-lg border p-4">
                    <div class="flex items-center justify-between mb-4">
                        <h2 class="text-lg font-medium">{{ activeCategoryName || 'Все объекты' }}</h2>
                        <div class="flex items-center gap-2">
                            <Input 
                                v-model="searchTerm" 
                                placeholder="Поиск по названию или артикулу"
                                class="w-[250px]" 
                            >
                                <template #prefix>
                                    <Search class="h-4 w-4 text-muted-foreground" />
                                </template>
                            </Input>
                            <Button @click="handleAddProduct">
                                <Plus class="h-4 w-4 mr-2" />
                                Добавить объект
                            </Button>
                        </div>
                    </div>
                    <DataTable 
                        :columns="columns" 
                        :data="filteredProducts" 
                        :isLoading="isLoadingProducts" 
                        :page="page" 
                        :page_size="page_size" 
                        :total="total" 
                        @update:page="handlePageChange" 
                        @update:page_size="handlePageSizeChange"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useAuthAccess } from '@/composables/useAuthAccess'
import { useSettingsStore } from '@/store/settings'
import { useDictionaryStore } from '@/store/dictionary'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus, Search } from 'lucide-vue-next'
import DataTable from '@/components/products/data-table.vue'
import { columns, type Product, type ProductUnit } from '@/components/products/columns'
import CategoryTree from '@/components/custom/CategoryTree.vue'
import Country from '@/components/custom/Country.vue'
import { getCategoriesListTree, getProductsList } from '@/api/core'
import { useModalStore } from '@/store/modal'
import { Category } from '@/types'
import Skeleton from '@/views/partners/Skeleton.vue'
const { hasPermission, hasAnyPermission, hasRole, hasAnyRole } = useAuthAccess()
const settingsStore = useSettingsStore();
const dictionaryStore = useDictionaryStore();
const modal = useModalStore();
const categoriesTree = ref([])

// Products Mock Data
const products = ref<Product[]>([])

// Active Category
const activeCategoryId = ref('')
const searchTerm = ref('')
const isLoadingCategories = ref(false)
const isLoadingProducts = ref(false)

//products table
const page = ref(1)
const page_size = ref(20)
const total = ref(0)

// Active category name
const activeCategoryName = computed(() => {
    if (activeCategoryId.value === '') return ''
    
    const findCategoryName = (categories: any[]) => {
        for (const category of categories) {
            if (category.id === activeCategoryId.value) {
                return category.name
            }
            if (category.children?.length) {
                const name = findCategoryName(category.children)
                if (name) return name
            }
        }
        return ''
    }
    
    return findCategoryName(categoriesTree.value)
})


// Filter products based on active category and search term
const filteredProducts = computed(() => {
    let result = products.value
    
    // Filter by search term
    if (searchTerm.value) {
        const search = searchTerm.value.toLowerCase()
        result = result.filter(p => 
            p.name.toLowerCase().includes(search) || 
            p.sku.toLowerCase().includes(search) ||
            p.description?.toLowerCase().includes(search)
        )
    }
    
    return result
})

// Event handlers
const updateCountry = (country: string) => {
    console.log('Selected country:', country)
    loadCategories()
}

const handleAddProduct = () => {
    modal.openModal('CreateUpdateProductModal', {
        category_id: activeCategoryId.value,
    }).then((result) => {
        console.log(result)
        loadProducts(activeCategoryId.value)
    }).catch((err) => {
        console.log(err)
    })
}
const handleCategoryChange = (categoryId: number) => {
    activeCategoryId.value = categoryId
    console.log('Selected category ID:', categoryId)
    // In a real app, you might fetch products for this category from the API
}

const handlePageChange = (page: number) => {
    loadProducts(activeCategoryId.value, page)
}
const handlePageSizeChange = (page_size: number) => {
    loadProducts(activeCategoryId.value, page.value, page_size)
}
const country_id = computed(() => {
    return dictionaryStore.dictionary.countries.find(country => country.code === settingsStore.country)?.id
})

const loadCategories = async () => {
    if (!country_id.value) return;
    isLoadingCategories.value = true
    const response = await getCategoriesListTree(country_id.value)
    isLoadingCategories.value = false
    if (response.status === 200) {
        console.log(response.data) //items
        categoriesTree.value = response.data.items
        if (categoriesTree.value.length > 0) {
            activeCategoryId.value = categoriesTree.value[0].id
        } else {
            activeCategoryId.value = ''
            products.value = []
        }
    }
}

const loadProducts = async (_id: string, _page: number = 1, _page_size: number = page_size.value) => {
    isLoadingProducts.value = true
    getProductsList({
        category_id: _id,
        page: _page,
        page_size: _page_size,
    }).then((response) => {
        console.log(response)
        total.value = response.data.total
        page.value = response.data.page
        page_size.value = response.data.page_size
        products.value = response.data.items.map((item) => {
            return {
                ...item,
                currency: settingsStore.currency.symbol || "-",
            }
        })
    }).finally(() => {
        isLoadingProducts.value = false
    })
}
const handleAddCategory = (category: Category) => {
    console.log(category)
    modal.openModal('CreateUpdateCategoryModal', {
        category: category,
        country_id: country_id.value,
    }).then((result) => {
        console.log(result)
        loadCategories()
    }).catch((err) => {
        console.log(err)
    })
}

onMounted(async () => {
    loadCategories()
})

watch(
    () => activeCategoryId.value,
    (newVal) => {
        if (newVal) {
            loadProducts(newVal)
        }
    }
)

</script>
